import { Panel, SeatRing } from "@ravens/ui";
import { useCallback, useEffect, useState, type CSSProperties, type DragEvent, type KeyboardEvent } from "react";
import { ApiError, getRoom, type RoomParticipant, type RoomView } from "../api.js";
import { invitationQr } from "../invite.js";
import { EventStage, publicEventsAt } from "../components/EventStage.js";
import { connectRoomFeed } from "../room-live.js";

const phaseCopy: Record<string, { eyebrow: string; title: string; detail: string }> = {
  ROLE_REVEAL: { eyebrow: "保持安静", title: "身份正在揭晓", detail: "请只看自己的手机" },
  FIRST_NIGHT: { eyebrow: "首夜", title: "村庄已经沉睡", detail: "系统正在依次唤醒角色" },
  OTHER_NIGHT: { eyebrow: "夜晚", title: "请闭眼并保持安静", detail: "需要行动时手机会提示" },
  DAY_DISCUSSION: { eyebrow: "白天", title: "自由讨论", detail: "分享线索，也可以暂时隐瞒身份" },
  NOMINATION: { eyebrow: "白天", title: "提名仍在继续", detail: "请在自己的手机上发起提名" },
  VOTING: { eyebrow: "公开投票", title: "请看向被提名者", detail: "所有人在手机上同时举手" },
  GAME_OVER: { eyebrow: "终局", title: "钟声停止", detail: "今夜的身份即将全部揭晓" },
};

function phaseLabel(phase: string | undefined, day: number | undefined): string {
  if (!phase) return "等待开局";
  if (phase === "ROLE_REVEAL") return "身份确认";
  if (phase === "FIRST_NIGHT") return "首夜 · 夜晚";
  if (phase === "OTHER_NIGHT") return `第 ${Math.max(day ?? 1, 1)} 夜 · 夜晚`;
  if (phase === "GAME_OVER") return "游戏结束";
  return `第 ${Math.max(day ?? 1, 1)} 天 · 白天`;
}

export function Display({ code, onBack, onRoomClosed }: { code: string; onBack: () => void; onRoomClosed: () => void }) {
  const [room, setRoom] = useState<RoomView>();
  const [fullscreen, setFullscreen] = useState(Boolean(document.fullscreenElement));
  const [qr, setQr] = useState<string>();
  const [seatError, setSeatError] = useState<string>();
  const acceptRoom = useCallback((incoming: RoomView) => setRoom((previous) => {
    if (incoming.game?.gameId && previous?.game?.gameId === incoming.game.gameId && (incoming.game.revision ?? 0) < (previous.game.revision ?? 0)) return previous;
    return incoming;
  }), []);
  useEffect(() => {
    let active = true;
    const refresh = () => void getRoom(code, "DISPLAY").then((next) => { if (active) { acceptRoom(next); setSeatError(undefined); } }).catch((error) => {
      if (active && error instanceof ApiError && [401, 403, 404].includes(error.status)) onRoomClosed();
      else if (active) setSeatError("连接暂时中断，正在重新同步城镇…");
    });
    refresh(); const timer = window.setInterval(refresh, 1_200);
    return () => { active = false; window.clearInterval(timer); };
  }, [code, onRoomClosed]);
  useEffect(() => { let active = true; void invitationQr(code, 320).then((value) => { if (active) setQr(value); }).catch(() => undefined); return () => { active = false; }; }, [code]);
  useEffect(() => {
    const update = () => setFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener("fullscreenchange", update);
    return () => document.removeEventListener("fullscreenchange", update);
  }, []);
  useEffect(() => {
    if (typeof WebSocket === "undefined") return;
    return connectRoomFeed(code, "DISPLAY", (payload) => acceptRoom(payload.room));
  }, [code]);

  const game = room?.game;
  const players = [...(room?.participants.filter((participant) => participant.mode === "PLAYER") ?? [])]
    .sort((left, right) => left.seat! - right.seat!);
  const seats = game?.seats ?? players.map((participant) => ({ seat: participant.seat!, nickname: participant.nickname, alive: true, connected: participant.connected, ghostVoteAvailable: true }));
  const copy = phaseCopy[game?.phase ?? "ROLE_REVEAL"] ?? phaseCopy.ROLE_REVEAL!;
  const nominee = game?.nomination ? seats.find((seat) => seat.seat === game.nomination!.nomineeSeat) : undefined;
  const nominator = game?.nomination ? seats.find((seat) => seat.seat === game.nomination!.nominatorSeat) : undefined;
  const publicEvents = publicEventsAt(game?.events ?? [], game?.serverNow ?? Date.now()).slice(-3).reverse();
  const isNight = game?.phase === "FIRST_NIGHT" || game?.phase === "OTHER_NIGHT";
  const finalePending = game?.phase === "GAME_OVER" && (game.presentationUntil ?? 0) > (game.serverNow ?? Date.now());
  const defending = Boolean(game?.nomination?.defenseUntil && (game.serverNow ?? Date.now()) < game.nomination.defenseUntil);
  const voteDetail = game?.nomination && nominee ? defending ? `${nominee.nickname} 的辩护时间 · ${Math.max(0, Math.ceil(((game.nomination.defenseUntil ?? 0) - (game.serverNow ?? Date.now())) / 1000))} 秒后开始顺时针计票` : game.nomination.voterOrder ? `${game.nomination.currentVoterSeat ? `正在计 ${game.nomination.currentVoterSeat} 号的票` : "等待钟声"} · 已计 ${game.nomination.countedSeats?.length ?? 0}/${game.nomination.voterOrder.length} 位 · 当前 ${game.nomination.votesRaised} 票 · 达标 ${game.nomination.threshold} 票` : `${nominator?.nickname ?? "一位玩家"} 发起提名 · ${nominee.nickname}（${nominee.seat}号） · 已投 ${game.nomination.votesReceived}/${seats.length} · 达标需 ${game.nomination.threshold} 票` : copy.detail;

  return <main className={`display display--${game?.phase.toLowerCase() ?? "lobby"}`}>
    {game ? <EventStage game={game} /> : null}
    <header className="display__header"><span>鸦钟夜话 · {code}</span><div><span className={`display__phase display__phase--${isNight ? "night" : "day"}`}><span aria-hidden="true">{isNight ? "☾" : "☀"}</span>{phaseLabel(game?.phase, game?.day)}</span><button type="button" onClick={onBack}>返回首页</button><button type="button" onClick={() => void (fullscreen ? document.exitFullscreen() : document.documentElement.requestFullscreen())}>{fullscreen ? "退出全屏" : "进入全屏"}</button></div></header>
    <section className="display__town"><SeatRing seats={seats} {...(game?.nomination?.currentVoterSeat ? { activeSeat: game.nomination.currentVoterSeat } : {})} {...(game?.nomination?.countedVotes ? { countedVotes: game.nomination.countedVotes } : {})} {...(game?.nomination?.raisedSeats ? { raisedSeats: game.nomination.raisedSeats } : {})} /><div className="display__center">{!game ? <div className="display__join">{qr ? <img src={qr} alt={`加入房间 ${code} 的二维码`} /> : null}<p>扫码加入 · 房间 {code}</p><h1>{players.length}/{room?.playerCount ?? "?"} 位已入座</h1><small>请房主在手机上调整座位，准备后开局</small></div> : <><p>{defending ? "辩护时间" : game.phase === "VOTING" ? "顺时针表决" : copy.eyebrow}</p><h1>{finalePending ? "钟声仍在回响" : game.phase === "GAME_OVER" ? `${game.winner === "GOOD" ? "善良" : "邪恶"}获胜` : nominee ? nominee.nickname : copy.title}</h1><small>{voteDetail}</small></>}</div></section>
    {game ? <Panel className="display__village-feed" aria-label="村庄公开信息">
      <header><div><span aria-hidden="true">⌁</span><strong>村庄公开信息</strong></div><small>所有玩家均可得知</small></header>
      <ol>{publicEvents.map((event, index) => <li key={event.seq} className={index === 0 ? "is-latest" : ""}><span>{index === 0 ? "最新" : `记录 ${event.seq}`}</span><p>{event.message}</p></li>)}</ol>
    </Panel> : <Panel className={`display__notice${seatError ? " display__notice--error" : ""}`} aria-live="polite">{seatError ?? (players.length > 1 ? "座位与准备状态由玩家手机同步 · 没有大屏也能完整游玩" : `${players.length}/${room?.playerCount ?? "?"} 位玩家已入座`)}</Panel>}
  </main>;
}

export function SeatOrderEditor({ players, busy, onReorder }: { players: RoomParticipant[]; busy: boolean; onReorder: (participantIds: string[]) => void }) {
  const [draggingId, setDraggingId] = useState<string>();
  const orderedIds = players.map((player) => player.id);

  function move(sourceId: string, targetIndex: number) {
    const sourceIndex = orderedIds.indexOf(sourceId);
    if (busy || sourceIndex < 0 || targetIndex < 0 || targetIndex >= orderedIds.length || sourceIndex === targetIndex) return;
    const next = [...orderedIds];
    next.splice(sourceIndex, 1);
    next.splice(targetIndex, 0, sourceId);
    onReorder(next);
  }

  function drop(event: DragEvent<HTMLLIElement>, targetIndex: number) {
    event.preventDefault();
    const sourceId = event.dataTransfer.getData("text/plain") || draggingId;
    if (sourceId) move(sourceId, targetIndex);
    setDraggingId(undefined);
  }

  function moveWithKeyboard(event: KeyboardEvent<HTMLLIElement>, playerId: string, index: number) {
    if (!["ArrowLeft", "ArrowUp", "ArrowRight", "ArrowDown"].includes(event.key)) return;
    event.preventDefault();
    const offset = event.key === "ArrowLeft" || event.key === "ArrowUp" ? -1 : 1;
    move(playerId, index + offset);
  }

  return <ol className="rn-seat-ring display-seat-editor" aria-label="开局座位顺序" style={{ "--seat-count": Math.max(players.length, 1) } as CSSProperties}>
    {players.map((player, index) => <li
      key={player.id}
      className={`rn-seat display-seat-editor__seat${draggingId === player.id ? " is-dragging" : ""}`}
      style={{ "--seat-index": index } as CSSProperties}
      draggable={!busy}
      tabIndex={0}
      aria-label={`${player.nickname}，当前 ${index + 1} 号，可拖动调整座位`}
      onDragStart={(event) => { event.dataTransfer.effectAllowed = "move"; event.dataTransfer.setData("text/plain", player.id); setDraggingId(player.id); }}
      onDragEnd={() => setDraggingId(undefined)}
      onDragOver={(event) => { event.preventDefault(); event.dataTransfer.dropEffect = "move"; }}
      onDrop={(event) => drop(event, index)}
      onKeyDown={(event) => moveWithKeyboard(event, player.id, index)}
    >
      <span className="rn-seat__number">{index + 1}号</span>
      <span className="rn-seat__name">{player.nickname}</span>
      <small className="display-seat-editor__handle" aria-hidden="true">⠿ 拖动</small>
    </li>)}
  </ol>;
}
